import { without } from 'es-toolkit';
import React, { useState } from 'react';
import { Alert, FormControlLabel, Stack, Switch, TextField } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { STATION_ROLES, StationRole } from '@paragon/contract-library';
import { hasScheduleAccess } from '@paragon/login-tools';
import { OptionalTooltip } from '@paragon/material';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { humanCase } from '@paragon/utilities';

import { createStation, setStationName } from '../../actions/supervisorActions';
import { useNavigateIfNeeded } from '../../util/useNavigateIfNeeded';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ParentSelect } from '../interface/ParentSelect';
import { EditStationDialogProps } from './dialogs';

export function EditStationDialog(props: EditStationDialogProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigateIfNeeded();

  const stationsState = useAppSelector((state) => state.stations);
  const userHasScheduleAccess = useAppSelector(hasScheduleAccess);

  const [didFail, setDidFail] = useState(false);

  let name: string | undefined;
  const [editedName, setEditedName] = useState<string>();
  const [nameProblem, setNameProblem] = useState<string>();

  const [stationRole, setStationRole] = useState<StationRole>('Assembly');

  let lineGuid: string | undefined;
  const [selectedLineGuid, setSelectedLineGuid] = useState<string>();

  const [followsSchedule, setFollowsSchedule] = useState<boolean>(false);

  if (props.mode === 'create') {
    name = editedName;
    lineGuid = selectedLineGuid ?? props.lineGuid;
  } else {
    const station = stationsState.stations[props.stationGuid];
    if (stationsState.status !== 'loaded' || station == null) return null;

    name = editedName ?? station.name;
    // lineGuid doesn't matter in edit mode, because the select will not be visible
    // followsSchedule should not be changed during the lifetime of a station, so it is only available during creation
  }

  async function onSubmit() {
    if (props.mode === 'edit' && editedName == null) {
      dispatch(closeCurrentPopup());
      return;
    }

    if (name == null || name.trim() === '') {
      setNameProblem('name is required');
      return;
    }

    setDidFail(false);
    setNameProblem(undefined);

    try {
      switch (props.mode) {
        case 'create':
          await dispatch(createStation(name, stationRole, followsSchedule, lineGuid));
          break;

        case 'edit':
          await dispatch(setStationName(props.stationGuid, name));
          break;
      }
    } catch (error) {
      setDidFail(true);

      console.error(error);

      return;
    }

    dispatch(closeCurrentPopup());

    if (props.mode === 'create' && lineGuid) {
      navigate(`/lines/${lineGuid}`);
    }
  }

  const actionString = props.mode === 'create' ? 'Create' : 'Rename';

  return (
    <Dialog
      title={`${actionString} station`}
      submitText={actionString}
      onSubmit={onSubmit}
      onCancel="close"
      enterKeySubmits
    >
      <Stack spacing={2} sx={{ marginTop: 1 }}>
        {didFail && (
          <Alert variant="outlined" severity="error">
            Unable to {actionString.toLowerCase()} station.
          </Alert>
        )}

        <TextField
          size="small"
          autoFocus
          autoComplete="off"
          label="Station Name"
          sx={{ marginTop: 1 }}
          value={name}
          error={!!nameProblem}
          onChange={(event) => setEditedName(event.target.value)}
          onFocus={(event) => event.target.select()}
        />

        {props.mode === 'create' && (
          <>
            <ParentSelect
              includeLines
              label="Line"
              optional={!lineGuid}
              parentGuid={lineGuid}
              onChange={(guid) => setSelectedLineGuid(guid ?? undefined)}
            />

            {userHasScheduleAccess && (
              <OptionalTooltip title="Only avaliable for stations on a line" visible={!lineGuid}>
                <FormControlLabel
                  disabled={!lineGuid}
                  sx={{ marginLeft: 0 }}
                  label="Follows Schedule"
                  control={
                    <Switch
                      checked={followsSchedule}
                      onChange={(event) => {
                        if (!event.target.checked && stationRole === 'Bundler') {
                          // Right now Bundler stations are only supported for stations that follow the schedule
                          setStationRole('Assembly');
                        }
                        setFollowsSchedule(event.target.checked);
                      }}
                    />
                  }
                />
              </OptionalTooltip>
            )}

            <PropertySelectField<StationRole>
              property="Role"
              value={stationRole}
              submit={(newStationRole) => void setStationRole(newStationRole)}
              options={followsSchedule ? STATION_ROLES : without(STATION_ROLES, 'Bundler')}
              getName={humanCase}
            />
          </>
        )}
      </Stack>
    </Dialog>
  );
}
